import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  GripVertical,
  LoaderCircle,
  X,
} from 'lucide-react';
import { useId, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from '../../../domain/tools/types';
import { formatBytes, formatNumber } from '../../../lib/format';
import '../workspace.css';

export type FileQueueItemStatus = 'ready' | 'validating' | 'warning' | 'error';

export interface FileQueueItem {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly mimeType?: string;
  readonly pageCount?: number;
  readonly status?: FileQueueItemStatus;
  readonly statusLabel?: string;
  readonly message?: string;
  readonly disabled?: boolean;
}

export interface FileQueueProps {
  readonly items: readonly FileQueueItem[];
  readonly disabled?: boolean;
  readonly reorderable?: boolean;
  readonly removable?: boolean;
  readonly onReorder?: (orderedIds: readonly string[]) => void;
  readonly onRemove?: (id: string) => void;
}

interface SortableFileProps {
  readonly item: FileQueueItem;
  readonly index: number;
  readonly total: number;
  readonly locale: Locale;
  readonly disabled: boolean;
  readonly reorderable: boolean;
  readonly removable: boolean;
  readonly onMove: (id: string, offset: -1 | 1) => void;
  readonly onRemove?: (id: string) => void;
}

function statusIcon(status: FileQueueItemStatus | undefined) {
  if (status === 'validating') return <LoaderCircle className="workspace-spin" size={17} />;
  if (status === 'error' || status === 'warning') return <AlertTriangle size={17} />;
  return <CheckCircle2 size={17} />;
}

function SortableFile({
  item,
  index,
  total,
  locale,
  disabled,
  reorderable,
  removable,
  onMove,
  onRemove,
}: SortableFileProps) {
  const { t } = useTranslation();
  const sortable = useSortable({
    id: item.id,
    disabled: disabled || item.disabled || !reorderable,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  const pageCount = item.pageCount === undefined
    ? undefined
    : t('common.counts.pages', { count: item.pageCount });

  return (
    <li
      ref={sortable.setNodeRef}
      className="workspace-file"
      data-dragging={sortable.isDragging || undefined}
      data-status={item.status ?? 'ready'}
      style={style}
    >
      {reorderable && (
        <button
          ref={sortable.setActivatorNodeRef}
          className="workspace-file__drag"
          type="button"
          disabled={disabled || item.disabled}
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`${t('common.labels.fileOrder')}: ${item.name}`}
          aria-roledescription={t('common.labels.fileOrder')}
        >
          <GripVertical size={19} aria-hidden="true" />
        </button>
      )}

      <span className="workspace-file__type" aria-hidden="true">
        <FileText size={21} />
      </span>

      <span className="workspace-file__body">
        <bdi className="workspace-file__name" dir="auto">{item.name}</bdi>
        <span className="workspace-file__meta">
          {pageCount && <span>{pageCount}</span>}
          <span>{formatBytes(item.size, locale)}</span>
          {item.mimeType && <bdi dir="ltr">{item.mimeType}</bdi>}
        </span>
        {item.message && (
          <span className="workspace-file__message" role={item.status === 'error' ? 'alert' : undefined}>
            {item.message}
          </span>
        )}
      </span>

      <span
        className="workspace-file__status"
        data-tone={item.status ?? 'ready'}
        title={item.statusLabel}
      >
        <span aria-hidden="true">{statusIcon(item.status)}</span>
        {item.statusLabel && <span className="workspace-file__status-label">{item.statusLabel}</span>}
      </span>

      {reorderable && (
        <span className="workspace-file__move-actions">
          <button
            type="button"
            disabled={disabled || item.disabled || index === 0}
            aria-label={t('workflow.fileList.moveFileEarlier', { name: item.name })}
            onClick={() => onMove(item.id, -1)}
          >
            <ChevronUp size={17} aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={disabled || item.disabled || index === total - 1}
            aria-label={t('workflow.fileList.moveFileLater', { name: item.name })}
            onClick={() => onMove(item.id, 1)}
          >
            <ChevronDown size={17} aria-hidden="true" />
          </button>
        </span>
      )}

      {removable && onRemove && (
        <button
          className="workspace-file__remove"
          type="button"
          disabled={disabled || item.disabled}
          aria-label={t('workflow.fileList.removeFile', { name: item.name })}
          onClick={() => onRemove(item.id)}
        >
          <X size={18} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

function localeFromLanguage(language: string): Locale {
  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function FileQueue({
  items,
  disabled = false,
  reorderable = true,
  removable = true,
  onReorder,
  onRemove,
}: FileQueueProps) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage ?? i18n.language);
  const titleId = useId();
  const [announcement, setAnnouncement] = useState('');
  const canReorder = reorderable && onReorder !== undefined;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const itemIds = useMemo(() => items.map(({ id }) => id), [items]);
  const itemById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const move = (id: string, offset: -1 | 1) => {
    const oldIndex = itemIds.indexOf(id);
    const newIndex = oldIndex + offset;
    if (oldIndex < 0 || newIndex < 0 || newIndex >= itemIds.length) return;

    const nextIds = arrayMove(itemIds, oldIndex, newIndex);
    onReorder?.(nextIds);
    const item = itemById.get(id);
    if (item) {
      setAnnouncement(t('workflow.fileList.movedFile', {
        name: item.name,
        position: formatNumber(newIndex + 1, locale),
        total: formatNumber(items.length, locale),
      }));
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = itemIds.indexOf(String(active.id));
    const newIndex = itemIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder?.(arrayMove(itemIds, oldIndex, newIndex));
  };

  const announcements = useMemo<Announcements>(() => ({
    onDragStart: ({ active }) => {
      const item = itemById.get(String(active.id));
      return item ? `${t('workflow.fileList.title')}: ${item.name}` : undefined;
    },
    onDragOver: () => undefined,
    onDragEnd: ({ active, over }) => {
      const item = itemById.get(String(active.id));
      const position = over ? itemIds.indexOf(String(over.id)) + 1 : 0;
      if (!item || position < 1) return t('common.status.canceled');
      return t('workflow.fileList.movedFile', {
        name: item.name,
        position: formatNumber(position, locale),
        total: formatNumber(items.length, locale),
      });
    },
    onDragCancel: () => t('common.status.canceled'),
  }), [itemById, itemIds, items.length, locale, t]);

  const screenReaderInstructions = useMemo<ScreenReaderInstructions>(() => ({
    draggable: `${t('common.actions.moveEarlier')}. ${t('common.actions.moveLater')}.`,
  }), [t]);

  if (items.length === 0) {
    return <p className="workspace-empty">{t('workflow.fileList.empty')}</p>;
  }

  return (
    <section className="workspace-file-queue" aria-labelledby={titleId}>
      <div className="workspace-section-heading">
        <h2 id={titleId}>{t('workflow.fileList.title')}</h2>
        <span>{t('common.counts.files', { count: items.length })}</span>
      </div>
      <DndContext
        accessibility={{ announcements, screenReaderInstructions }}
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <ol className="workspace-file-list" aria-label={t('a11y.fileList')}>
            {items.map((item, index) => (
              <SortableFile
                key={item.id}
                item={item}
                index={index}
                total={items.length}
                locale={locale}
                disabled={disabled}
                reorderable={canReorder && items.length > 1}
                removable={removable}
                onMove={move}
                onRemove={onRemove}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      <p className="workspace-visually-hidden" aria-live="polite">{announcement}</p>
    </section>
  );
}
